/* AFTER HOURS — storefront interactions.
   Plain JavaScript, no dependencies. Loaded with `defer`.
   Components initialise from data attributes and re-initialise on theme editor section loads. */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const desktop = window.matchMedia('(min-width: 990px)');

  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const announce = (message) => {
    const region = document.getElementById('A11yStatus');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 60);
  };

  /* ---------- Money ---------- */
  function formatMoney(cents, format = theme.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) || 0;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      const fixed = (number / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    const match = format.match(placeholder);
    const type = match ? match[1] : 'amount';
    let formatted;
    switch (type) {
      case 'amount_no_decimals': formatted = withDelimiters(value, 0); break;
      case 'amount_with_comma_separator': formatted = withDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': formatted = withDelimiters(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': formatted = withDelimiters(value, 2, "'", '.'); break;
      default: formatted = withDelimiters(value, 2);
    }
    return format.replace(placeholder, formatted);
  }

  /* ---------- Dialogs (native <dialog>) ---------- */
  function openDialog(dialog, opener) {
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.classList.remove('is-closing');
    dialog.showModal();
    qsa(`[aria-controls="${dialog.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'true'));
    dialog.dispatchEvent(new CustomEvent('ah:dialog-open', { bubbles: true }));
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      dialog.classList.remove('is-closing');
      dialog.close();
      qsa(`[aria-controls="${dialog.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'false'));
      const opener = dialog._opener;
      if (opener && document.contains(opener) && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
      dialog.dispatchEvent(new CustomEvent('ah:dialog-close', { bubbles: true }));
    };
    if (reduceMotion.matches) return finish();
    dialog.classList.add('is-closing');
    dialog.addEventListener('animationend', function handler(event) {
      if (event.target !== dialog) return;
      dialog.removeEventListener('animationend', handler);
      finish();
    });
    window.setTimeout(finish, 450);
  }

  function initDialog(dialog) {
    if (dialog._ahInit) return;
    dialog._ahInit = true;
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog(dialog);
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog(dialog);
    });
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen);
      if (dialog) {
        event.preventDefault();
        initDialog(dialog);
        qsa('dialog[open]').forEach((d) => { if (d !== dialog) closeDialog(d); });
        openDialog(dialog, opener);
      }
      return;
    }
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      event.preventDefault();
      closeDialog(closer.closest('dialog'));
    }
  });

  desktop.addEventListener('change', (event) => {
    if (event.matches) closeDialog(document.getElementById('MenuDrawer'));
  });

  /* ---------- Header ---------- */
  function initHeader(root) {
    const header = qs('[data-header]', root);
    if (!header || header._ahInit) return;
    header._ahInit = true;

    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    qsa('[data-dropdown]', header).forEach((item) => {
      const toggle = qs('[data-dropdown-toggle]', item);
      let hoverOpenedAt = 0;
      let leaveTimer;
      const set = (open) => {
        item.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', String(open));
      };
      toggle.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');
        if (isOpen && Date.now() - hoverOpenedAt < 700) return;
        set(!isOpen);
      });
      item.addEventListener('mouseenter', () => {
        if (!canHover.matches) return;
        clearTimeout(leaveTimer);
        if (!item.classList.contains('is-open')) hoverOpenedAt = Date.now();
        set(true);
      });
      item.addEventListener('mouseleave', () => {
        if (!canHover.matches) return;
        leaveTimer = setTimeout(() => set(false), 160);
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) set(false);
      });
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && item.classList.contains('is-open')) {
          set(false);
          toggle.focus();
        }
      });
    });

    document.addEventListener('click', (event) => {
      qsa('[data-dropdown].is-open', header).forEach((item) => {
        if (!item.contains(event.target)) {
          item.classList.remove('is-open');
          qs('[data-dropdown-toggle]', item).setAttribute('aria-expanded', 'false');
        }
      });
    });

    qsa('dialog[data-ah-dialog]', root).forEach(initDialog);
  }

  /* ---------- Announcement bar ---------- */
  function initAnnouncement(bar) {
    const slides = qsa('[data-slide]', bar);
    if (slides.length < 2) return;
    let index = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));
    let timer;
    const speed = Number(bar.dataset.speed || 5) * 1000;
    const autoplay = bar.dataset.autoplay === 'true' && !reduceMotion.matches;
    const live = qs('[data-track]', bar);

    const show = (i) => {
      index = (i + slides.length) % slides.length;
      slides.forEach((slide, n) => {
        const active = n === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        qsa('a, button', slide).forEach((el) => { el.tabIndex = active ? 0 : -1; });
      });
    };
    const stop = () => clearInterval(timer);
    const start = () => {
      stop();
      if (autoplay) timer = setInterval(() => show(index + 1), speed);
    };

    qs('[data-prev]', bar)?.addEventListener('click', () => { live?.setAttribute('aria-live', 'polite'); show(index - 1); start(); });
    qs('[data-next]', bar)?.addEventListener('click', () => { live?.setAttribute('aria-live', 'polite'); show(index + 1); start(); });
    bar.addEventListener('mouseenter', stop);
    bar.addEventListener('mouseleave', start);
    bar.addEventListener('focusin', stop);
    bar.addEventListener('focusout', start);
    bar.addEventListener('shopify:block:select', (event) => {
      const i = slides.indexOf(event.target.closest('[data-slide]'));
      if (i > -1) { stop(); show(i); }
    });
    show(index);
    start();
  }

  /* ---------- Scroll reveal ---------- */
  let revealObserver;
  function initReveal(root = document) {
    const items = qsa('[data-reveal]:not(.is-revealed)', root);
    if (!items.length) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window) || document.documentElement.classList.contains('shopify-design-mode')) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    revealObserver = revealObserver || new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
    items.forEach((el) => revealObserver.observe(el));
  }

  /* ---------- Age confirmation (self-declared, not identity verification) ---------- */
  function initAgeGate() {
    const gate = qs('[data-age-gate]');
    const html = document.documentElement;
    if (!gate || gate._ahInit) return;
    gate._ahInit = true;
    const days = Number(gate.dataset.days || 30);
    const siblings = () => qsa('body > *').filter((el) => !el.contains(gate) && el.tagName !== 'SCRIPT');

    const activate = () => {
      siblings().forEach((el) => { el.inert = true; });
      const confirm = qs('[data-age-confirm]', gate);
      window.requestAnimationFrame(() => confirm && confirm.focus({ preventScroll: true }));
    };

    const remember = () => {
      try { window.localStorage.setItem('ah_age_confirmed', String(Date.now())); } catch (e) { /* storage blocked */ }
      document.cookie = `ah_age=1; max-age=${Math.round(days * 86400)}; path=/; SameSite=Lax`;
    };

    gate.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') event.preventDefault();
      if (event.key !== 'Tab') return;
      const focusables = qsa('a[href], button:not([disabled])', gate);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });

    qs('[data-age-confirm]', gate)?.addEventListener('click', () => {
      remember();
      siblings().forEach((el) => { el.inert = false; });
      const finish = () => {
        html.classList.remove('age-gate-pending');
        gate.classList.remove('is-leaving');
        const target = document.getElementById('MainContent');
        if (target) { target.setAttribute('tabindex', '-1'); target.focus({ preventScroll: true }); }
      };
      if (reduceMotion.matches) return finish();
      gate.classList.add('is-leaving');
      window.setTimeout(finish, 560);
    });

    qs('[data-age-exit]', gate)?.addEventListener('click', (event) => {
      event.preventDefault();
      window.location.href = gate.dataset.exitUrl || 'https://www.google.com';
    });

    if (html.classList.contains('age-gate-pending')) activate();
  }

  /* ---------- Cart ---------- */
  const Cart = {
    drawer: () => document.getElementById('CartDrawer'),
    usesDrawer: () => theme.cartType !== 'page' && !!document.getElementById('CartDrawer'),

    sectionIds() {
      const ids = ['cart-drawer'];
      const page = qs('[data-cart-page]');
      if (page) ids.push(page.dataset.sectionId);
      return ids;
    },

    async request(url, body, isForm = false) {
      const response = await fetch(url, {
        method: 'POST',
        headers: isForm ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
          : { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: isForm ? body : JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        const error = new Error(data.description || data.message || strings.cartError || 'Something went wrong.');
        error.data = data;
        throw error;
      }
      return data;
    },

    async add(formData) {
      formData.append('sections', this.sectionIds().join(','));
      formData.append('sections_url', window.location.pathname);
      const data = await this.request(`${routes.cartAdd}.js`, formData, true);
      this.render(data.sections);
      return data;
    },

    async change(line, quantity) {
      const data = await this.request(`${routes.cartChange}.js`, {
        line, quantity, sections: this.sectionIds(), sections_url: window.location.pathname,
      });
      this.render(data.sections);
      return data;
    },

    render(sections) {
      if (!sections) return;
      const active = document.activeElement;
      const activeId = active && active.id;
      const drawerHTML = sections['cart-drawer'];
      if (drawerHTML) {
        const doc = parseHTML(drawerHTML);
        const next = doc.getElementById('CartDrawerInner');
        const current = document.getElementById('CartDrawerInner');
        if (next && current) {
          current.replaceWith(next);
          this.updateCount(Number(next.dataset.cartCount || 0));
        }
      }
      const page = qs('[data-cart-page]');
      if (page && sections[page.dataset.sectionId]) {
        const doc = parseHTML(sections[page.dataset.sectionId]);
        const next = doc.getElementById('MainCartContent');
        const current = document.getElementById('MainCartContent');
        if (next && current) current.replaceWith(next);
      }
      // Keep keyboard focus on the same control after re-rendering (or the drawer's close button).
      if (active && !active.isConnected) {
        const target = (activeId && document.getElementById(activeId)) || qs('#CartDrawer[open] [data-dialog-close]') || qs('#MainCartContent h1');
        if (target) target.focus({ preventScroll: true });
      }
    },

    updateCount(count) {
      qsa('[data-cart-bubble]').forEach((bubble) => {
        bubble.textContent = count;
        bubble.hidden = count === 0;
        bubble.classList.remove('is-bumped');
        void bubble.offsetWidth;
        bubble.classList.add('is-bumped');
      });
      qsa('[data-cart-label]').forEach((label) => {
        label.textContent = (strings.cartCount || 'Cart, {{ count }} items').replace('{{ count }}', count);
      });
    },

    open(opener) {
      const drawer = this.drawer();
      if (!drawer) return;
      initDialog(drawer);
      qsa('dialog[open]').forEach((d) => { if (d !== drawer) closeDialog(d); });
      openDialog(drawer, opener);
    },
  };

  // Product & quick-add forms
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-product-form]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-form-error]');
    if (error) { error.hidden = true; error.textContent = ''; }
    button?.classList.add('is-loading');
    button?.setAttribute('aria-busy', 'true');
    try {
      await Cart.add(new FormData(form));
      if (Cart.usesDrawer()) {
        Cart.open(button);
        announce(strings.addedToCart || 'Added to cart');
      } else {
        window.location.href = routes.cart;
      }
    } catch (err) {
      if (error) { error.textContent = err.message; error.hidden = false; }
      announce(err.message);
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-busy');
    }
  });

  // Cart quantity / remove (drawer + page)
  const pendingChanges = new Map();
  async function changeLine(line, quantity, item) {
    item?.classList.add('is-updating');
    try {
      await Cart.change(line, quantity);
      announce(quantity === 0 ? (strings.itemRemoved || 'Item removed') : (strings.cartUpdated || 'Cart updated'));
    } catch (err) {
      item?.classList.remove('is-updating');
      const target = item?.querySelector('[data-line-error]');
      if (target) { target.textContent = err.message; target.hidden = false; }
      announce(err.message);
    }
  }

  document.addEventListener('click', (event) => {
    const cartLink = event.target.closest('[data-cart-open]');
    if (cartLink && Cart.usesDrawer() && !qs('[data-cart-page]')) {
      event.preventDefault();
      Cart.open(cartLink);
      return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      changeLine(Number(remove.dataset.line), 0, remove.closest('[data-cart-item]'));
      return;
    }
    const step = event.target.closest('[data-qty-step]');
    if (step) {
      const wrapper = step.closest('[data-quantity]');
      const input = wrapper && qs('input', wrapper);
      if (!input) return;
      event.preventDefault();
      const min = Number(input.min || 0);
      const max = input.max ? Number(input.max) : Infinity;
      const next = Math.min(max, Math.max(min, (Number(input.value) || 0) + Number(step.dataset.qtyStep)));
      if (next === Number(input.value)) return;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-qty]');
    if (input) {
      const line = Number(input.dataset.line);
      clearTimeout(pendingChanges.get(line));
      pendingChanges.set(line, setTimeout(() => {
        changeLine(line, Math.max(0, Number(input.value) || 0), input.closest('[data-cart-item]'));
      }, 350));
      return;
    }
    const note = event.target.closest('[data-cart-note]');
    if (note) {
      fetch(`${routes.cart}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note.value }),
      }).catch(() => {});
    }
  });

  /* ---------- Product page ---------- */
  class Gallery {
    constructor(root, section) {
      this.root = root;
      this.section = section;
      this.track = qs('[data-gallery-track]', root);
      this.slides = qsa('[data-gallery-slide]', root);
      this.thumbs = qsa('[data-gallery-thumb]', root);
      this.prev = qs('[data-gallery-prev]', root);
      this.next = qs('[data-gallery-next]', root);
      this.counter = qs('[data-gallery-counter]', root);
      this.lightbox = qs('[data-lightbox]', section);
      this.index = 0;
      if (!this.track) return;

      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.goTo(i));
        thumb.addEventListener('keydown', (event) => this.onThumbKey(event, i));
      });
      this.prev?.addEventListener('click', () => this.goTo(this.index - 1));
      this.next?.addEventListener('click', () => this.goTo(this.index + 1));
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.index + 1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.index - 1); }
      });
      let frame;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
          if (i !== this.index) this.setActive(i);
        });
      }, { passive: true });
      qsa('[data-gallery-zoom]', root).forEach((button) => {
        button.addEventListener('click', () => this.openLightbox(Number(button.dataset.index)));
      });
      if (this.lightbox) this.initLightbox();
      this.setActive(0);
    }

    onThumbKey(event, i) {
      const vertical = desktop.matches;
      const nextKey = vertical ? 'ArrowDown' : 'ArrowRight';
      const prevKey = vertical ? 'ArrowUp' : 'ArrowLeft';
      let target = null;
      if (event.key === nextKey) target = Math.min(this.thumbs.length - 1, i + 1);
      if (event.key === prevKey) target = Math.max(0, i - 1);
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = this.thumbs.length - 1;
      if (target === null) return;
      event.preventDefault();
      this.thumbs[target].focus();
      this.goTo(target);
    }

    goTo(i, smooth = true) {
      const index = Math.max(0, Math.min(this.slides.length - 1, i));
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto' });
      this.setActive(index);
    }

    goToMedia(mediaId) {
      const i = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (i > -1) this.goTo(i);
    }

    setActive(i) {
      this.index = i;
      this.thumbs.forEach((thumb, n) => thumb.setAttribute('aria-current', String(n === i)));
      this.slides.forEach((slide, n) => slide.setAttribute('aria-hidden', String(n !== i)));
      const activeThumb = this.thumbs[i];
      if (activeThumb) {
        const list = activeThumb.parentElement.parentElement;
        const t = activeThumb.parentElement;
        if (desktop.matches) list.scrollTo({ top: t.offsetTop - list.clientHeight / 2 + t.clientHeight / 2, behavior: 'smooth' });
        else list.scrollTo({ left: t.offsetLeft - list.clientWidth / 2 + t.clientWidth / 2, behavior: 'smooth' });
      }
      if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
      if (this.prev) this.prev.disabled = i === 0;
      if (this.next) this.next.disabled = i === this.slides.length - 1;
    }

    initLightbox() {
      const lb = this.lightbox;
      initDialog(lb);
      this.lbTrack = qs('[data-lightbox-track]', lb);
      this.lbSlides = qsa('[data-lightbox-slide]', lb);
      this.lbCounter = qs('[data-lightbox-counter]', lb);
      this.lbIndex = 0;
      const go = (i) => {
        const index = Math.max(0, Math.min(this.lbSlides.length - 1, i));
        this.lbTrack.scrollTo({ left: index * this.lbTrack.clientWidth, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      };
      qs('[data-lightbox-prev]', lb)?.addEventListener('click', () => go(this.lbIndex - 1));
      qs('[data-lightbox-next]', lb)?.addEventListener('click', () => go(this.lbIndex + 1));
      lb.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); go(this.lbIndex + 1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); go(this.lbIndex - 1); }
      });
      this.lbTrack.addEventListener('scroll', () => {
        const i = Math.round(this.lbTrack.scrollLeft / Math.max(1, this.lbTrack.clientWidth));
        if (i !== this.lbIndex) {
          this.lbIndex = i;
          this.lbSlides.forEach((s) => s.classList.remove('is-zoomed'));
          if (this.lbCounter) this.lbCounter.textContent = `${i + 1} / ${this.lbSlides.length}`;
        }
      }, { passive: true });
      this.lbSlides.forEach((slide) => {
        const img = qs('img', slide);
        if (!img) return;
        img.addEventListener('click', (event) => {
          const zoomed = slide.classList.toggle('is-zoomed');
          if (zoomed) this.setOrigin(img, event);
        });
        img.addEventListener('pointermove', (event) => {
          if (slide.classList.contains('is-zoomed')) this.setOrigin(img, event);
        });
      });
      lb.addEventListener('ah:dialog-close', () => {
        this.lbSlides.forEach((s) => s.classList.remove('is-zoomed'));
        this.goTo(this.lbIndex, false);
      });
    }

    setOrigin(img, event) {
      const rect = img.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = `${x}% ${y}%`;
    }

    openLightbox(i) {
      if (!this.lightbox) return;
      openDialog(this.lightbox, this.slides[i] && qs('[data-gallery-zoom]', this.slides[i]));
      this.lbIndex = i;
      requestAnimationFrame(() => {
        this.lbTrack.scrollTo({ left: i * this.lbTrack.clientWidth, behavior: 'auto' });
        if (this.lbCounter) this.lbCounter.textContent = `${i + 1} / ${this.lbSlides.length}`;
      });
    }
  }

  class ProductSection {
    constructor(root) {
      this.root = root;
      const json = qs('[data-product-json]', root);
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.form = qs('form[data-product-form]', root);
      this.idInput = this.form && this.form.querySelector('input[name="id"]');
      this.picker = qs('[data-variant-picker]', root);
      this.submit = this.form && this.form.querySelector('[type="submit"]');
      this.sticky = qs('[data-sticky-atc]', root);
      const galleryEl = qs('[data-gallery]', root);
      this.gallery = galleryEl ? new Gallery(galleryEl, root) : null;

      if (this.picker) {
        this.picker.addEventListener('change', () => this.onOptionChange());
        this.updateAvailability(this.selectedOptions());
      }
      this.initSticky();
    }

    selectedOptions() {
      return qsa('[data-option]', this.picker).map((group) => {
        const select = qs('select', group);
        if (select) return select.value;
        const checked = qs('input:checked', group);
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      return this.variants.find((variant) => variant.options.every((value, i) => value === options[i]));
    }

    updateAvailability(selected) {
      qsa('[data-option]', this.picker).forEach((group, position) => {
        qsa('input[type="radio"], option', group).forEach((input) => {
          const candidate = selected.slice();
          candidate[position] = input.value;
          const available = this.variants.some((variant) => variant.available && variant.options.every((value, i) => value === candidate[i]));
          if (input.tagName === 'OPTION') {
            const base = input.dataset.label || input.value;
            input.textContent = available ? base : `${base} — ${strings.unavailable || 'Unavailable'}`;
          } else {
            input.classList.toggle('is-unavailable', !available);
          }
        });
        const label = qs('[data-selected-value]', group);
        if (label) label.textContent = selected[position] || '';
      });
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      const variant = this.findVariant(selected);
      this.updateAvailability(selected);
      this.updateButtons(variant);
      if (!variant) return;
      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.updatePrice(variant);
      if (variant.featured_media && this.gallery) this.gallery.goToMedia(variant.featured_media.id);
      if (this.root.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    updatePrice(variant) {
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      const html = `<span class="visually-hidden">${onSale ? (strings.salePrice || 'Sale price') : (strings.price || 'Price')}</span>`
        + `<span class="price__current">${formatMoney(variant.price)}</span>`
        + (onSale ? `<s class="price__compare"><span class="visually-hidden">${strings.regularPrice || 'Regular price'}</span>${formatMoney(variant.compare_at_price)}</s>` : '');
      qsa('[data-price]', this.root).forEach((el) => { el.innerHTML = html; });
      qsa('[data-sticky-price]', this.root).forEach((el) => { el.textContent = formatMoney(variant.price); });
      qsa('[data-sticky-variant]', this.root).forEach((el) => { el.textContent = variant.title === 'Default Title' ? '' : variant.title; });
    }

    updateButtons(variant) {
      const available = !!(variant && variant.available);
      const label = !variant ? (strings.unavailable || 'Unavailable') : available ? (strings.addToCart || 'Add to cart') : (strings.soldOut || 'Sold out');
      [this.submit, qs('[data-sticky-submit]', this.root)].forEach((button) => {
        if (!button) return;
        button.disabled = !available;
        const text = qs('[data-button-text]', button);
        if (text) text.textContent = label; else button.textContent = label;
      });
      qsa('[data-stock]', this.root).forEach((el) => {
        const threshold = Number(el.dataset.lowThreshold || 0);
        let qty = 0;
        let state = 'in_stock';
        const entry = (el.dataset.stockMap || '')
          .split('|')
          .map((pair) => pair.split(':'))
          .find((pair) => variant && pair[0] === String(variant.id));
        if (!variant) state = 'unavailable';
        else if (entry) { state = entry[1]; qty = Number(entry[2]) || 0; }
        else if (!available) state = 'sold_out';
        el.classList.toggle('is-sold-out', state === 'sold_out');
        el.classList.toggle('is-low', state === 'low');
        el.classList.toggle('is-backorder', state === 'backorder');
        el.textContent =
          state === 'unavailable' ? (strings.unavailable || 'Unavailable')
          : state === 'sold_out' ? (strings.soldOut || 'Sold out')
          : state === 'backorder' ? (strings.backorder || 'Available to order')
          : state === 'low' ? (strings.lowStock || 'Only {{ count }} left').replace('{{ count }}', qty)
          : (strings.inStock || 'Available');
      });
      const payment = qs('.shopify-payment-button', this.root);
      if (payment) payment.hidden = !available;
    }

    initSticky() {
      if (!this.sticky || !this.submit) return;
      const setVisible = (visible) => {
        if (this.sticky.classList.contains('is-visible') === visible && this._stickyInit) return;
        this._stickyInit = true;
        this.sticky.classList.toggle('is-visible', visible);
        this.sticky.inert = !visible;
        document.body.classList.toggle('sticky-atc-visible', visible);
        qsa('.bottom-nav').forEach((nav) => nav.classList.toggle('is-hidden', visible));
      };
      // Show once the main Add to cart button has scrolled up behind the sticky header.
      const update = () => {
        const headerBottom = (document.querySelector('[data-header]')?.getBoundingClientRect().bottom || 0) + 8;
        setVisible(this.submit.getBoundingClientRect().bottom < headerBottom);
      };
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { ticking = false; update(); });
      }, { passive: true });
      window.addEventListener('resize', update);
      update();
      qs('[data-sticky-submit]', this.sticky)?.addEventListener('click', () => {
        if (this.form.requestSubmit) this.form.requestSubmit(this.submit); else this.submit.click();
      });
    }
  }

  /* ---------- Collection & search filters ---------- */
  class Facets {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.form = qs('[data-facets-form]', section);
      this.panel = qs('[data-facets]', section);
      if (!this.form) return;

      const apply = () => this.apply();
      const debounced = debounce(apply, 300);
      const debouncedPrice = debounce(apply, 800);
      this.form.addEventListener('input', (event) => {
        if (event.target.type === 'number') debouncedPrice(); else debounced();
      });
      this.form.addEventListener('submit', (event) => { event.preventDefault(); apply(); });
      qsa(`[form="${this.form.id}"]`).forEach((control) => control.addEventListener('change', apply));

      section.addEventListener('click', (event) => {
        const link = event.target.closest('[data-facets-link]');
        if (link) {
          event.preventDefault();
          this.render(link.href);
          return;
        }
        if (event.target.closest('[data-facets-open]')) this.toggle(true);
        if (event.target.closest('[data-facets-close]')) this.toggle(false);
      });
      this.panel?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.toggle(false);
      });
      desktop.addEventListener('change', () => this.syncInert());
      this.syncInert();
      window.addEventListener('popstate', () => this.render(window.location.href, false));
    }

    syncInert() {
      if (!this.panel) return;
      this.panel.inert = !desktop.matches && !this.panel.classList.contains('is-open');
    }

    toggle(open) {
      if (!this.panel) return;
      this.panel.classList.toggle('is-open', open);
      document.documentElement.classList.toggle('is-locked', open);
      qsa('[data-facets-open]', this.section).forEach((b) => b.setAttribute('aria-expanded', String(open)));
      this.syncInert();
      if (open) {
        this._opener = document.activeElement;
        qs('[data-facets-close]', this.panel)?.focus();
      } else if (this._opener) {
        this._opener.focus();
      }
    }

    apply() {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(this.form)) {
        if (value !== '') params.append(key, value);
      }
      const url = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
      this.render(url);
    }

    async render(url, push = true) {
      const results = qs('[data-facets-results]', this.section);
      results?.classList.add('is-loading');
      try {
        const fetchUrl = new URL(url, window.location.origin);
        fetchUrl.searchParams.set('section_id', this.sectionId);
        const response = await fetch(fetchUrl.toString());
        if (!response.ok) throw new Error(response.statusText);
        const doc = parseHTML(await response.text());
        const swap = (selector) => {
          const next = qs(selector, doc);
          const current = qs(selector, this.section);
          if (next && current) current.innerHTML = next.innerHTML;
        };
        const open = qsa('[data-facets-filters] details[open]', this.section).map((d) => d.dataset.index);
        const focused = document.activeElement && document.activeElement.id;
        swap('[data-facets-results]');
        swap('[data-facets-filters]');
        qsa('[data-facets-count]', this.section).forEach((el) => {
          const next = qs('[data-facets-count]', doc);
          if (next) el.innerHTML = next.innerHTML;
        });
        qsa('[data-facets-active-count]', this.section).forEach((el) => {
          const next = qs('[data-facets-active-count]', doc);
          el.textContent = next ? next.textContent : '';
          el.hidden = !next || !next.textContent.trim() || next.textContent.trim() === '0';
        });
        qsa('[data-facets-filters] details', this.section).forEach((d) => {
          if (open.includes(d.dataset.index)) d.open = true;
        });
        if (focused) document.getElementById(focused)?.focus({ preventScroll: true });
        if (push) window.history.pushState({}, '', url);
        initReveal(this.section);
        const count = qs('[data-facets-count]', this.section);
        if (count) announce(count.textContent.trim());
      } catch (error) {
        window.location.href = url;
      } finally {
        results?.classList.remove('is-loading');
      }
    }
  }

  /* ---------- Predictive search ---------- */
  function initPredictiveSearch(root) {
    const input = qs('[data-predictive-input]', root);
    const results = qs('[data-predictive-results]', root);
    if (!input || !results || !routes.predictiveSearch) return;
    let controller;
    const run = debounce(async () => {
      const q = input.value.trim();
      if (!q) {
        results.innerHTML = '';
        results.hidden = true;
        return;
      }
      controller?.abort();
      controller = new AbortController();
      results.setAttribute('aria-busy', 'true');
      try {
        const url = `${routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,query&resources[limit]=4&resources[limit_scope]=each&section_id=predictive-search`;
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(response.statusText);
        const doc = parseHTML(await response.text());
        const content = qs('[data-predictive-content]', doc);
        results.innerHTML = content ? content.innerHTML : '';
        results.hidden = false;
        const status = qs('[data-predictive-status]', results);
        if (status) announce(status.textContent.trim());
      } catch (error) {
        if (error.name !== 'AbortError') results.hidden = true;
      } finally {
        results.removeAttribute('aria-busy');
      }
    }, 260);
    input.addEventListener('input', run);
  }

  /* ---------- Product recommendations ---------- */
  function initRecommendations(el) {
    if (el._ahInit || !el.dataset.url) return;
    el._ahInit = true;
    const load = async () => {
      try {
        const response = await fetch(el.dataset.url);
        const doc = parseHTML(await response.text());
        const next = qs('[data-recommendations]', doc);
        if (next && next.innerHTML.trim().length && qs('.card', next)) {
          el.innerHTML = next.innerHTML;
          el.hidden = false;
          initReveal(el);
        } else {
          el.closest('.shopify-section')?.setAttribute('hidden', '');
        }
      } catch (error) {
        el.closest('.shopify-section')?.setAttribute('hidden', '');
      }
    };
    if (!('IntersectionObserver' in window)) return load();
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { io.disconnect(); load(); }
    }, { rootMargin: '0px 0px 600px 0px' });
    io.observe(el);
  }


  /* ---------- Delivery estimate ---------- */
  const DELIVERY_KEY = 'ah_delivery_destination';
  function initDelivery(el) {
    if (el._ahInit) return;
    el._ahInit = true;
    const select = el.querySelector('[data-delivery-select]');
    const output = el.querySelector('[data-delivery-estimate]');
    if (!select || !output) return;
    let stored = null;
    try { stored = localStorage.getItem(DELIVERY_KEY); } catch (error) { stored = null; }
    if (stored) {
      const match = Array.from(select.options).find((option) => option.textContent.trim() === stored);
      if (match) select.value = match.value;
    }
    const update = () => {
      const option = select.options[select.selectedIndex];
      if (!option) return;
      output.textContent = option.value;
      try { localStorage.setItem(DELIVERY_KEY, option.textContent.trim()); } catch (error) { /* private mode */ }
    };
    select.addEventListener('change', update);
    update();
  }

  /* ---------- Init ---------- */
  function init(root = document) {
    initHeader(root);
    qsa('[data-localization-select]', root).forEach((select) => {
      if (select._ahInit) return;
      select._ahInit = true;
      select.addEventListener('change', () => select.form && select.form.submit());
    });
    qsa('[data-announcement]', root).forEach((el) => { if (!el._ahInit) { el._ahInit = true; initAnnouncement(el); } });
    qsa('[data-product-section]', root).forEach((el) => { if (!el._ahInit) { el._ahInit = true; el._product = new ProductSection(el); } });
    qsa('[data-facets-section]', root).forEach((el) => { if (!el._ahInit) { el._ahInit = true; el._facets = new Facets(el); } });
    qsa('[data-predictive-search]', root).forEach((el) => { if (!el._ahInit) { el._ahInit = true; initPredictiveSearch(el); } });
    qsa('[data-recommendations]', root).forEach(initRecommendations);
    qsa('[data-delivery]', root).forEach(initDelivery);
    qsa('dialog[data-ah-dialog]', root).forEach(initDialog);
    initReveal(root);
  }

  initAgeGate();
  init();

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    initAgeGate();
  });

  window.AfterHours = { Cart, openDialog, closeDialog, formatMoney };
})();
